const { Injectable } = require('@angular/core');
import transmitigator from '@ally/transmitigator';
import { Subject } from 'rxjs/Subject';
import { ApplicationProxyService } from './applicationproxy.service';
import { journeyDestinations } from '../models/Constants/journey-destinations.constant';
@Injectable()
export class TransmitService {
  constructor(appProxy: ApplicationProxyService) {
    this.appProxy = appProxy;
    this.transmitObj$ = new Subject();

    this.transmitObj$.subscribe((val) => {
      this.transmitObj = val;
    });

    this.userName = '';
    this.currentToken = '';
    this.incomingTicketID = '';
    this.currentRefreshTimer = undefined;
    this.journeyVersion = 'v2';
    this.refreshInterval = 270000; // 4.5 minutes
    this.transmitigator = undefined;
  }

  getTransmitObjObservable() {
    return this.transmitObj$.asObservable();
  }

  setTransmitObj(val) {
    this.transmitObj$.next(val);
  }

  getIncomingTicketID() {
    return this.incomingTicketID;
  }

  setIncomingTicketID(val) {
    this.incomingTicketID = val;
  }

  setUserName(value) {
    if (this.userName) {
      return;
    }
    this.userName = value;
  }

  setTransmitToken(accessToken) {
    if (!accessToken) {
      return;
    }
    this.currentToken = accessToken;
    const tokenExpiry = new Date(
      new Date().getTime() + this.refreshInterval * 60000
    );
    this.currentTokenExpiry = tokenExpiry;
    this.currentRefreshTimer = setTimeout(() => {
      this.refreshTransmitToken(accessToken);
    }, this.refreshInterval);
  }

  async refreshTransmitToken(token) {
    const options = {
      access_token: token,
      username: this.userName,
      journey_version: this.journeyVersion,
    };
    try {
      if (!options.access_token) {
        throw new Error('refreshAccessToken missing required token to refresh');
      }
      const { refreshAccessToken } = await this.getTransmitigatorInstance();
      const data = await refreshAccessToken(options);
      if (!data.session) {
        throw new Error('refreshAccessToken response missing session object');
      }
      const accessToken = data.session.access_token;
      if (!accessToken) {
        throw new Error('refreshAccessToken response missing access_token');
      }
      this.setTransmitToken(accessToken);
    } catch (error) {
      console.log(
        `${new Date().toLocaleTimeString()} transmit.service refreshTransmitToken() \n`,
        { options, error: { message: error.message, stack: error.stack } }
      );
    }
  }

  initializeTransmitSession(payload) {
    return new Promise(async (resolve, reject) => {
      try {
        const tokenResponse = await this.getTokenFromTicketId(payload.ticketID);
        if (tokenResponse.access_token && tokenResponse.userNamePvtEncrypt) {
          const { access_token, userNamePvtEncrypt } = tokenResponse;
          this.setUserName(userNamePvtEncrypt);
          this.setTransmitToken(access_token);
          const response = await this.appProxy.initializeSession(
            access_token,
            payload.relayState,
            userNamePvtEncrypt
          );
          if (!response.data.success) {
            throw new Error('Unable to authenticate with apigee');
          }
          return resolve(response.data.location);
        }
        throw new Error('Unable to get access_token');
      } catch (error) {
        return reject(error);
      }
    });
  }

  getTransmitigatorInstance() {
    if (!this.transmitigator) {
      const application = {
        id: 'ALLYUSBOLB',
        name: 'AOB',
        version: '1.0',
        apigeeApiKey: this.transmitObj.apiKey,
      };
      this.transmitigator = transmitigator({
        server: this.transmitObj.server,
        application,
      });
    }
    return Promise.resolve(this.transmitigator);
  }

  async getTokenFromTicketId(ticketID) {
    const { rehydrate } = await this.getTransmitigatorInstance();

    return new Promise(async (resolve, reject) => {
      try {
        const response = await rehydrate({
          ticketID,
          journey_version: this.journeyVersion,
        });
        if (response && response.session && response.session.access_token) {
          return resolve(response.session);
        }
        throw new Error('Unable to exchange ticketID');
      } catch (error) {
        return reject(error);
      }
    });
  }

  async generateTicketId() {
    const params = {
      destination: journeyDestinations.bankReact,
      journey_version: this.journeyVersion,
      username: this.userName,
      access_token: this.currentToken,
    };
    const { transferTicket } = await this.getTransmitigatorInstance();
    const ticketId = await transferTicket(params);
    return ticketId;
  }

  async transmitigatorLogout() {
    const { logout } = await this.getTransmitigatorInstance();
    await logout();
    return;
  }
}
